import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Facebook,
  Instagram,
  Loader2,
  MessagesSquare,
  Sparkles,
} from "lucide-react";
import Button from "@/components/Button";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";

/**
 * Mensajes de Instagram y Facebook Messenger con Carmen (5-oct-2026).
 *
 * El negocio entra con su Facebook, elige su página (y el Instagram enlazado)
 * y desde entonces esos mensajes llegan a Conversaciones, donde contesta
 * Carmen o el equipo. Se pinta en las pestañas de Facebook e Instagram de
 * Integraciones.
 *
 * Es un complemento de pago ("Redes sociales IA"): quien lo tiene ve la
 * conexión; quien no, la oferta para contratarlo; y si aún no está en venta,
 * no se pinta nada.
 *
 * Con `platform` es la tarjeta del SUPERADMIN: conecta el Instagram y la
 * página de ReservaManager, cuyos mensajes van a Soporte → Conversaciones.
 * Ahí no hay complemento ni sede: solo cambian las direcciones y los textos.
 */

interface SocialConnection {
  pageName: string;
  instagramUsername: string | null;
  hasInstagram: boolean;
  messengerAiEnabled: boolean;
  instagramAiEnabled: boolean;
  needsReconnect: boolean;
  connectedAt: string;
}

/** El complemento tal como se vende (null = todavía no está en venta). */
interface SocialOffer {
  id: string;
  name: string;
  price: number;
}

interface PendingPage {
  id: string;
  name: string;
  instagramUsername: string | null;
  hasInstagram: boolean;
}

/** Por qué no se pudo conectar, tal como lo devuelve el servidor en la URL. */
const CONNECT_ERROR_MESSAGES: Record<string, string> = {
  cancelled: "Has cancelado la conexión en Facebook. No se ha cambiado nada.",
  no_pages:
    "Tu cuenta de Facebook no ha dado acceso a ninguna página. Vuelve a conectar y marca la página del negocio.",
  page_in_other_location: "Esa página ya está conectada a otra de tus sedes. Desconéctala allí primero.",
  page_in_other_business: "Esa página ya está conectada a otro negocio de ReservaManager.",
  subscribe_failed:
    "Facebook no nos ha dejado recibir los mensajes de esa página. Comprueba que eres administrador de la página y vuelve a intentarlo.",
  not_contracted: "Tu negocio no tiene activo el complemento de redes sociales.",
  invalid_state: "La conexión ha caducado. Vuelve a pulsar «Conectar».",
};
const GENERIC_CONNECT_ERROR = "No se ha podido completar la conexión con Facebook. Vuelve a intentarlo.";

type SocialMessagingCardProps =
  | { platform?: false; locationId: string; locationName: string }
  | { platform: true };

/** Lo que cambia entre la tarjeta de un negocio y la del superadmin. */
function cardTexts(platformMode: boolean, locationName: string) {
  if (!platformMode) {
    return {
      title: "Mensajes de Instagram y Facebook con Carmen",
      intro:
        "Los mensajes directos de tu Instagram y de tu página de Facebook llegan a Conversaciones, junto a los de WhatsApp. Carmen los contesta y reserva; tu equipo puede tomar el control cuando quiera, y si contestas desde la app de Instagram o Facebook, Carmen se calla en esa conversación.",
      choosePage: `¿Cuál es la página de ${locationName}?`,
      choosePageHint: "Has dado acceso a varias páginas. Elige la de esta sede.",
      checklist: [
        "Eres administrador de la página de Facebook del negocio.",
        "Tu Instagram es una cuenta profesional y está enlazado a esa página.",
      ],
      teamAnswers: "Los mensajes llegan a Conversaciones y los contesta tu equipo.",
      pageInOtherBusiness: CONNECT_ERROR_MESSAGES.page_in_other_business,
    };
  }
  return {
    title: "Instagram y Facebook de ReservaManager",
    intro:
      "Los mensajes directos del Instagram y de la página de Facebook de ReservaManager llegan a Soporte → Conversaciones, junto a los de WhatsApp y el chat de ayuda. Carmen contesta dudas y precios e invita a probar la herramienta; el equipo puede tomar el control cuando quiera, y si contestáis desde la app de Instagram o Facebook, Carmen se calla en esa conversación.",
    choosePage: "¿Cuál es la página de ReservaManager?",
    choosePageHint: "Has dado acceso a varias páginas. Elige la de ReservaManager.",
    checklist: [
      "Eres administrador de la página de Facebook de ReservaManager.",
      "El Instagram de ReservaManager es una cuenta profesional y está enlazado a esa página.",
    ],
    teamAnswers: "Los mensajes llegan a Conversaciones y los contesta el equipo.",
    pageInOtherBusiness:
      "Esa página ya está conectada a un negocio. Desconéctala primero en sus Integraciones.",
  };
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        checked ? "bg-primary-600" : "bg-gray-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export default function SocialMessagingCard(props: SocialMessagingCardProps) {
  const platformMode = props.platform === true;
  const locationId = props.platform === true ? null : props.locationId;
  const texts = cardTexts(platformMode, props.platform === true ? "" : props.locationName);
  // Mismas llamadas en los dos casos; cambia la base y que la sede no viaja.
  const apiBase = platformMode ? "/admin/platform-social" : "/social";
  const scope = locationId ? { locationId } : {};
  const errorMessage = (code?: string | null): string =>
    code === "page_in_other_business"
      ? texts.pageInOtherBusiness
      : ((code && CONNECT_ERROR_MESSAGES[code]) ?? GENERIC_CONNECT_ERROR);

  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [contracted, setContracted] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [offer, setOffer] = useState<SocialOffer | null>(null);
  const [connection, setConnection] = useState<SocialConnection | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{ token: string; pages: PendingPage[] } | null>(null);
  // Facebook no nos ha enseñado ninguna página: suele ser porque la página
  // pertenece a una cuenta de empresa de Meta, y entonces exige el permiso
  // "Administrar tu empresa". No se pide de entrada (asusta y casi nunca hace
  // falta): solo aquí, explicando para qué es.
  const [needsBusinessPermission, setNeedsBusinessPermission] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await apiClient.get(`${apiBase}/connection`, {
        params: locationId ? { locationId } : undefined,
      });
      // La cuenta de la plataforma no depende de ningún complemento.
      setContracted(platformMode || Boolean(response.data?.data?.contracted));
      setSuspended(Boolean(response.data?.data?.suspended));
      setOffer(response.data?.data?.offer ?? null);
      setConnection(response.data?.data?.connection ?? null);
    } catch {
      // Sin datos no se enseña la tarjeta: es mejor que ofrecer un botón roto.
      setContracted(false);
      setSuspended(false);
      setOffer(null);
      setConnection(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, apiBase, platformMode]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Vuelta de Facebook: el servidor redirige aquí con el resultado en la URL.
  useEffect(() => {
    const connected = searchParams.get("social") === "connected";
    const error = searchParams.get("social_error");
    const pendingToken = searchParams.get("social_pending");
    if (!connected && !error && !pendingToken) return;

    if (connected) showToast("Instagram y Facebook conectados", "success");
    if (error === "no_pages_try_business") setNeedsBusinessPermission(true);
    else if (error) showToast(errorMessage(error), "error");
    if (pendingToken) {
      apiClient
        .get(`${apiBase}/connect/pending`, { params: { token: pendingToken } })
        .then((response) =>
          setPending({ token: pendingToken, pages: response.data?.data?.pages ?? [] }),
        )
        .catch(() => showToast(CONNECT_ERROR_MESSAGES.invalid_state, "error"));
    }

    // Se limpia la URL para que recargar la página no repita el aviso.
    const next = new URLSearchParams(searchParams);
    for (const key of ["social", "social_error", "social_pending", "social_location"]) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
    void load();
    // `errorMessage` solo depende del modo, que no cambia en la vida de la tarjeta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, showToast, load, apiBase]);

  const handleConnect = async (withBusinessPermission = false) => {
    setBusy(true);
    try {
      const response = await apiClient.get(`${apiBase}/connect/url`, {
        params: { ...scope, ...(withBusinessPermission ? { business: "1" } : {}) },
      });
      const url = response.data?.data?.url;
      if (typeof url !== "string") throw new Error("sin dirección");
      // Se sale a Facebook; al terminar vuelve a esta misma página.
      window.location.href = url;
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showToast(message ?? GENERIC_CONNECT_ERROR, "error");
      setBusy(false);
    }
  };

  const handleChoosePage = async (pageId: string) => {
    if (!pending) return;
    setBusy(true);
    try {
      await apiClient.post(`${apiBase}/connect/choose`, { token: pending.token, pageId });
      setPending(null);
      showToast("Instagram y Facebook conectados", "success");
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      showToast(errorMessage(code), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (field: "instagramAiEnabled" | "messengerAiEnabled") => {
    if (!connection) return;
    const nextValue = !connection[field];
    setBusy(true);
    setConnection({ ...connection, [field]: nextValue });
    try {
      await apiClient.put(`${apiBase}/connection`, { ...scope, [field]: nextValue });
    } catch {
      setConnection({ ...connection, [field]: !nextValue });
      showToast("No se ha podido guardar el cambio", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    const ok = await confirm({
      title: "Desconectar Instagram y Facebook",
      message:
        "Dejaremos de recibir los mensajes de esta página y de su Instagram. Las conversaciones que ya hay se conservan.",
      confirmText: "Desconectar",
      cancelText: "Cancelar",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiClient.delete(`${apiBase}/connection`, {
        params: locationId ? { locationId } : undefined,
      });
      setConnection(null);
      showToast("Desconectado", "success");
    } catch {
      showToast("No se ha podido desconectar", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  // Impago del complemento: la conexión se conserva, pero Carmen no contesta.
  if (!contracted && suspended) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Redes sociales IA está en pausa por un pago pendiente</p>
          <p className="mt-1">
            Mientras tanto no se atienden los mensajes de Instagram ni de Facebook.
          </p>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="font-medium underline mt-2"
          >
            Revisar la suscripción
          </button>
        </div>
      </div>
    );
  }

  // Sin contratar: la oferta, si el complemento ya está en venta.
  if (!contracted) {
    if (!offer) return null;
    return (
      <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white rounded-lg border border-primary-100 flex-shrink-0">
            <MessagesSquare className="w-6 h-6 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900">
              Carmen también en Instagram y Facebook
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Los mensajes directos de tu Instagram y de tu página de Facebook, atendidos al
              momento: Carmen resuelve dudas y gestiona reservas, y tu equipo ve cada
              conversación y toma el control cuando quiere.
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-600">
            Complemento <span className="font-medium text-gray-900">{offer.name}</span>
            {" · "}
            <span className="font-semibold text-gray-900">{offer.price}€/mes</span>
          </p>
          <Button
            onClick={() => navigate("/subscription")}
            className="inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Contratar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-4 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-white rounded-lg border border-primary-100 flex-shrink-0">
          <MessagesSquare className="w-6 h-6 text-primary-600" />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900">{texts.title}</h3>
          <p className="text-sm text-gray-600 mt-1">{texts.intro}</p>
        </div>
      </div>

      {pending && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <p className="font-medium text-gray-900">{texts.choosePage}</p>
          <p className="text-sm text-gray-500">{texts.choosePageHint}</p>
          <div className="space-y-2">
            {pending.pages.map((page) => (
              <button
                key={page.id}
                type="button"
                disabled={busy}
                onClick={() => handleChoosePage(page.id)}
                className="w-full flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-lg border border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-colors disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900 truncate">{page.name}</span>
                  <span className="block text-sm text-gray-500 truncate">
                    {page.hasInstagram
                      ? `Instagram: ${page.instagramUsername ? `@${page.instagramUsername}` : "enlazado"}`
                      : "Sin Instagram enlazado"}
                  </span>
                </span>
                <span className="text-sm font-medium text-primary-600 flex-shrink-0">Elegir</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!connection && !pending && needsBusinessPermission && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 space-y-3">
          <div className="flex items-start gap-2 text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-sm space-y-2">
              <p className="font-medium">Facebook no nos ha enseñado tu página</p>
              <p>
                Suele pasar cuando la página pertenece a una cuenta de empresa de Meta. En ese
                caso Facebook exige un permiso más, que verás como «Administrar tu empresa».
                Solo lo usamos para poder ver tu página y conectarla: no tocamos tus anuncios ni
                ningún otro dato de la empresa.
              </p>
              <p>
                Si no es tu caso, comprueba que al conectar marcaste la página del negocio y que
                eres su administrador.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={() => handleConnect(true)}
              disabled={busy}
              className="inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Facebook className="w-4 h-4" />}
              Conectar con ese permiso
            </Button>
            <button
              type="button"
              onClick={() => handleConnect()}
              disabled={busy}
              className="text-sm font-medium text-amber-900 underline disabled:opacity-50"
            >
              Volver a intentarlo sin él
            </button>
          </div>
        </div>
      )}

      {!connection && !pending && !needsBusinessPermission && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <p className="text-sm text-gray-600">Antes de conectar, comprueba que:</p>
          <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
            {texts.checklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Button
            onClick={() => handleConnect()}
            disabled={busy}
            className="inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Facebook className="w-4 h-4" />}
            Conectar Instagram y Facebook
          </Button>
        </div>
      )}

      {connection && (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {connection.needsReconnect && (
            <div className="p-3 flex items-start gap-2 bg-amber-50 text-amber-800 rounded-t-lg">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p>Facebook ha dejado de aceptar la conexión y los mensajes no se pueden contestar.</p>
                <button
                  type="button"
                  onClick={() => handleConnect()}
                  disabled={busy}
                  className="font-medium underline mt-1"
                >
                  Volver a conectar
                </button>
              </div>
            </div>
          )}

          <div className="p-3 flex items-center gap-2 text-sm text-gray-700">
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="min-w-0 truncate">
              Conectado a <span className="font-medium">{connection.pageName}</span>
              {connection.hasInstagram &&
                (connection.instagramUsername
                  ? ` y a @${connection.instagramUsername}`
                  : " y a su Instagram")}
            </span>
          </div>

          <div className="p-3 flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <Instagram className="w-4 h-4 text-pink-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-gray-900">Carmen contesta en Instagram</p>
                <p className="text-sm text-gray-500">
                  {!connection.hasInstagram
                    ? "Esta página no tiene un Instagram profesional enlazado."
                    : connection.instagramAiEnabled
                      ? "Carmen contesta los mensajes directos."
                      : texts.teamAnswers}
                </p>
              </div>
            </div>
            <Toggle
              label="Carmen contesta en Instagram"
              checked={connection.hasInstagram && connection.instagramAiEnabled}
              disabled={busy || !connection.hasInstagram}
              onChange={() => handleToggle("instagramAiEnabled")}
            />
          </div>

          <div className="p-3 flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <Facebook className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-gray-900">Carmen contesta en Facebook Messenger</p>
                <p className="text-sm text-gray-500">
                  {connection.messengerAiEnabled
                    ? "Carmen contesta los mensajes de la página."
                    : texts.teamAnswers}
                </p>
              </div>
            </div>
            <Toggle
              label="Carmen contesta en Facebook Messenger"
              checked={connection.messengerAiEnabled}
              disabled={busy}
              onChange={() => handleToggle("messengerAiEnabled")}
            />
          </div>

          <div className="p-3 flex justify-end">
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              Desconectar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
